<template>
  <FloatingBox
    v-if="visible"
    ref="floating_cont"
    v-click-outside="e => handleClickOutside(e)"
    referenceElId="gmf_vertical"
    :initialPlacement="pinned ? 'custom' : 'reference'"
    :initialReferenceOffset="{ x: -8, y: 8 }"
    :coordinates="coordinates"
    class="floating-gmf-selektor flgmfslctr anim-fade-in text-noselect"
    @update:coordinates="handleUpdateCoordinates"
  >
    <template #default="v">
      <ResizeBox
        style="--max-height: 98vh"
        :dimensions="dimensions"
        :min="minmax.min"
        :max="minmax.max"
        @reposition:mousedown="val => v.flbox_methods.handleMousedown(val.e, val)"
        @reposition:touchstart="val => v.flbox_methods.handleTouchstart(val.e, val)"
        @set:moveXY="val => v.flbox_methods.setMoveXY(val)"
        @update:dimensions="handleUpdateDimensions"
      >
        <template #default>
          <div
            ref="inner"
            class="flgmfslctr_inner"
          >
            <TabBox
              class="flgmfslctr_tabbox"
              :options="libOptions"
              :tab="libtab"
              translatePath="solutions"
              style="
                --tbbox-content-clr-back: var(--clr-black-1);
                --tbbox-overflow-y: hidden;
                --tbbox-overflow-x: hidden;
              "
              tabsStyle="
              --height: 2.75rem;
              --height-inner: 3rem;
              --tab-fnt-size: 1rem;
              --tab-radius: 8px;
              --padding-inner: 0 2.5rem 0 0;
              --clr-tbbox-tab-back: transparent;
              --inner-flex: 1;
            "
              :hasNamedTabSlot="true"
              :hasScrollRight="false"
              @click="
                item => {
                  item.fn()
                }
              "
              @touchstart="e => v.flbox_methods.handleTouchstart(e)"
              @mousedown="e => v.flbox_methods.handleMousedown(e)"
            >
              <template #inner_after>
                <a
                  v-if="!surveyTaken && surveyLink"
                  :href="surveyLink"
                  target="_blank"
                  class="clr-white surveylink ma-auto d-flex mw-0"
                  style="position: relative; top: -0.125rem"
                  title="Help us improve the solution selector. Take quick survey!"
                >
                  <GwMessage
                    :message="{
                      hide: true,
                      icon: 'mdi-rocket-launch',
                      type: 'info',
                    }"
                    style="
                      --back-opacity: 0.5;
                      --back-padding: 0.25rem 1rem;
                      --font-icon: 1.25rem;
                      --gap: 0.5rem;
                      --font-size: 0.875rem;
                      --close-font-size: 2rem;
                      border: 1px solid var(--clr-back);
                      font-weight: 600;
                    "
                    @click="takeSurvey"
                  >
                    <div class="truncate">Help us improve the selector. Take quick survey!</div>
                  </GwMessage>
                </a>
              </template>
              <template #tabs_after>
                <div class="flgmfslctr_title f-center cursor-grab">
                  <div class="flgmfslctr_title_controls">
                    <Checkbox
                      v-for="(item, idx) in controls"
                      :key="idx"
                      :data-tst="`gmf_selector_control_${item.tstId}`"
                      class="csst flgmfslctr_title_controls_item"
                      style="
                        --clr-disabled: var(--clr-grey-2);
                        --clr-enabled: var(--clr-primary);
                        --icon-size: 1.125em;
                      "
                      :value="item.value"
                      :icons="item.icons"
                      @click="item.onClick"
                      @mouseenter="fl_positionTooltip"
                    >
                      <template #content>
                        <div
                          class="csst_content clr-white"
                          style="position: fixed; line-height: 1.2; max-width: 28rem"
                          v-html="item.tooltip"
                        />
                      </template>
                    </Checkbox>
                  </div>
                  <div
                    class="icon_btn flgmfslctr_close mdi mdi-close pa-1 text-semi-large csst"
                    data-tst="btn_gmf_selector_close"
                    @click="close"
                    @mouseenter="fl_positionTooltip"
                  >
                    <div
                      class="csst_content clr-white text-nowrap"
                      style="position: fixed; line-height: 1.2; max-width: 28rem"
                    >
                      {{ $t('btn.close') }}
                    </div>
                  </div>
                </div>
              </template>
              <template #tab_gwiz>
                <i class="mdi mdi-bookshelf text-large mr-2"></i>
                {{ $t(`solutions.gwiz`) }}
              </template>
              <template #tab_ai_sols>
                <i class="mdi mdi-folder-plus text-large mr-2"></i>
                {{ $t(`solutions.custom`) }}
              </template>
              <template #tab_ai_trees>
                <i class="mdi mdi-folder-cog text-large mr-2"></i>
                {{ $t(`solutions.custom_settings`) }}
              </template>
              <template #tab_ai_reports>
                <i class="mdi mdi-folder text-large mr-2"></i>
                {{ $t(`solutions.custom_reports`) }}
              </template>
              <template #default="{ tab }">
                <GmfSelektor
                  v-if="tab === 'gwiz'"
                  ref="gmf_selector"
                  class="flgmfslctr_gmf-selector"
                  style="--gmfslctr-wrap: wrap"
                  :namespace
                  :channelId
                  :instaApply="true"
                  :keepHistoryTree="true"
                  :routerParams="{
                    push: true,
                    replace: false,
                    saveLocally: true,
                  }"
                  :hasPreview="false"
                  @table:click="handleTableClick"
                  @click.stop
                  @gmf:update="checkPosition"
                />
                <SolfigrDashboard
                  v-else
                  :instaApply="true"
                  :namespace
                  :channelId
                  :dimensions="dimensions"
                  :hasSavedSolutions="libtab === 'ai_sols'"
                  :hasCustomReports="libtab === 'ai_reports'"
                  :routerParams="{
                    push: true,
                    replace: false,
                    saveLocally: true,
                  }"
                  class="flgmfslctr_gmf-selector"
                  @close="handleTableClick"
                />
              </template>
            </TabBox>
          </div>
          <div
            v-if="tabIsGwiz && gmfHasPreview"
            ref="preview"
            class="flgmfslctr_preview"
          >
            <div class="flgmfslctr_preview_inner">
              <div class="f-center">
                <div
                  class="clr-grey uppercase"
                  style="font-size: 0.875em"
                >
                  {{ $t('common.preview') }}
                </div>
                <div
                  style="margin-right: -0.5rem"
                  class="icon_btn mdi ml-auto mdi-close text-semi-large csst"
                  @click="togglePreview"
                  @mouseenter="fl_positionTooltip"
                >
                  <div
                    class="csst_content clr-white text-nowrap"
                    style="position: fixed; line-height: 1.2; max-width: 28rem"
                  >
                    {{ $t('btn.close') }}
                  </div>
                </div>
              </div>
              <GmfPreview :gameformat />
            </div>
          </div>
        </template>
      </ResizeBox>
    </template>
  </FloatingBox>
</template>

<script>
import { nextTick } from 'vue'
import Checkbox from '@/components/Common/Checkbox/Checkbox'
import TabBox from '@/components/lib/TabBox'
import GwMessage from '@/components/Messages/GwMessage'
import { SNK } from '@/helpers/helpers'

import FloatingBox from '@/components/lib/FloatingBox'
import SolfigrDashboard from '@/components/ConfigureSolution/SolfigrDashboard'
import ResizeBox from '@/components/lib/ResizeBox'
import GmfSelektor from '@/components/GameFormat/GmfSelektor/GmfSelektor'
import GmfPreview from '@/components/GameFormat/GmfSelektor/GmfPreview'

import float_mix from '@/mixins/float_mix'

export default {
  name: 'FloatingGmfSelector',
  components: {
    GmfSelektor,
    FloatingBox,
    ResizeBox,
    GmfPreview,
    SolfigrDashboard,
    Checkbox,
    TabBox,
    GwMessage,
    // Snackbar,
  },
  mixins: [float_mix],
  props: {
    namespace: { default: '' },
    mobile: Boolean,
  },
  data() {
    this.channelId = `floating_gmf_${Date.now()}`
    return {
      pinned: false,
      hasPreview: 'unset',
      coordinates: { percentX: 0, percentY: 0 },
      dimensions: { height: 0, width: 0 },
      childWindow: null,

      // remove after survey
      surveyTaken: false,
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.closeDetached)

    // remove after survey
    if (!this.$store.getters.user_has_flag('GMF_SURVEY')) {
      this.surveyTaken = true
      return
    }
    this.surveyTaken = localStorage.getItem('surveyTaken3') === 'true'
    if (this.surveyTaken) return

    const surveyType = !this.$store.getters.user_info.subs.some(
      i => i.plan.tier.toLowerCase() === 'elite',
    )
      ? 'non_elite'
      : (
            this.$store.getters.api_get_data(
              'api_poker_custom_solution_table_initial_for_the_survey',
            )?.results || []
          ).length < 10
        ? 'elite_10_saved'
        : 'elite_10_plus_saved'

    // console.log('surveyType', surveyType, this.$store.getters.user_info.subs)

    this.surveyLink = {
      non_elite:
        'https://docs.google.com/forms/d/e/1FAIpQLScW75PxdwvBtDf-_u66kcCNubvH-O8Pe67V-gZlDjHfjdk7og/viewform?usp=sf_link',
      elite_10_saved:
        'https://docs.google.com/forms/d/e/1FAIpQLSfVtgRz6kXCAYak3nsF7RehG2edSfBpHEQ6Tq2kaV6qoYjlTA/viewform?usp=sf_link',
      elite_10_plus_saved:
        'https://docs.google.com/forms/d/e/1FAIpQLSfj0geHF13J6KS0tJkABeb9g4VgO_d3COeQHL9foPlD1S0mLQ/viewform?usp=sf_link',
    }[surveyType]
  },
  methods: {
    takeSurvey() {
      this.surveyTaken = true
      localStorage.setItem('surveyTaken3', 'true')
    },
    positionPreview(times = 0) {
      if (!this.gmfHasPreview || times === 5) return
      nextTick(() => {
        const referenceEl = this.$refs.inner
        const element = this.$refs.preview
        if (referenceEl && element) {
          const options = { offset: 0, padding: 0, placement: 'left-start' }
          this.fl_setPositionStyle({ referenceEl, element, options })
        }
      })
    },
    getCLickedLayer(el) {
      if (
        el?.getAttribute('data-gwmenu') === 'noclose' ||
        el?.getAttribute('data-layer') === 'dialog'
      ) {
        return el
      }
      if (!el?.parentElement) {
        return null
      }
      return this.getCLickedLayer(el.parentElement)
    },
    handleClickOutside(e) {
      if (this.getCLickedLayer(e.target)) return
      // debugger
      this.handleTableClick(e)
    },
    checkPosition() {
      if (this.$refs.floating_cont) {
        nextTick(() => {
          this.$refs.floating_cont.recomputePosition()
        })
      }
      this.positionPreview()
    },
    togglePreview() {
      this.gmfHasPreview = !this.gmfHasPreview
      this.checkPosition()
    },
    togglePinned() {
      this.pinned = !this.pinned
    },
    toggleKeepHistory() {
      this.gmfKeepHistory = !this.gmfKeepHistory
    },
    handleTableClick() {
      if (!this.pinned) {
        this.close()
      }
    },
    close() {
      this.$store.commit('globals_set', { key: 'floating_gmf_visible', value: false })
    },
    handleUpdateCoordinates({ percentX, percentY }) {
      this.coordinates = { percentX, percentY }
      this.positionPreview()
    },
    handleUpdateDimensions({ width, height }) {
      this.dimensions = { width, height }
      this.positionPreview()
    },
    closeDetached() {
      if (this.detachedRegistered) {
        this.$store.dispatch('bc_postMessage', {
          channelId: this.channelId,
          data: { code: 'any_window_close' },
        })
      }
    },
    handleOpenInNewClick() {
      if (this.detachedRegistered) {
        window.blur()
        this.childWindow.focus()
        return
      }
      const { channelId } = this
      this.$store.dispatch('bc_startChannel', { channelId })
      const query_gmfSelector = this.$store.getters[SNK('query_gmfSelector', this.namespace)]
      let params = {
        ...this.$route.query,
        channelId,
        ...query_gmfSelector,
        namespace: this.namespace,
      }
      params = new URLSearchParams(params)
      const url = `${window.location.origin}/detached-gmf-selector?${params}`
      const opts = `
        popup=yes,
        scrollbars=no,
        status=no,
        location=no,
        toolbar=no,
        menubar=no,
        width=1284,
        height=644,
        top=250,
        left=250,`
      this.childWindow = window.open(url, '_blank', opts)
    },
  },
  computed: {
    minmax() {
      const fntSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize)
      const res = {
        wsize: this.$store.getters.globals_window_size,
        min: {
          height: fntSizePx * 22,
          width: fntSizePx * 42,
        },
        max: {
          height: Infinity,
          width: fntSizePx * 72,
        },
      }
      return res
    },
    gmfHasPreview: {
      get() {
        const res =
          this.hasPreview !== 'unset'
            ? this.hasPreview
            : localStorage.getItem('gmfHasPreview') === 'true'
        return res
      },
      set(bool) {
        this.hasPreview = bool
        localStorage.setItem('gmfHasPreview', bool)
      },
    },
    gmfKeepHistory: {
      get() {
        const res = this.$store.getters.mergedUserSettings.gmfKeepHistory
        return res
      },
      set(value) {
        this.$store.dispatch('userSettings', { gmfKeepHistory: value })
      },
    },
    detachedRegistered() {
      const channelsInfo = this.$store.getters.bc_channels_info
      const events = channelsInfo[this.channelId]?.events || []
      const res = events[events.length - 1]?.status === 'online'
      return res
    },
    visible() {
      return this.$store.getters.floating_gmf_visible
    },
    gameformat() {
      return this.$store.getters[SNK('gameformat', this.namespace)]
    },
    controls() {
      return [
        {
          is_hidden: !this.tabIsGwiz,
          icons: ['mdi-restart', 'mdi-restart'],
          onClick: this.toggleKeepHistory,
          value: !this.gmfKeepHistory,
          tooltip: this.gmfKeepHistory
            ? this.$t('tooltips.always_reset_history')
            : this.$t('tooltips.keep_history'),
          tstId: 'reset_history',
        },
        {
          is_hidden: !this.tabIsGwiz,
          icons: ['mdi-eye', 'mdi-eye'],
          onClick: this.togglePreview,
          value: this.gmfHasPreview,
          tooltip: this.gmfHasPreview
            ? this.$t('tooltips.hide_preview')
            : this.$t('tooltips.show_preview'),
          tstId: 'show_preview',
        },
        {
          icons: ['mdi-open-in-new', 'mdi-open-in-new'],
          onClick: this.handleOpenInNewClick,
          value: this.detachedRegistered,
          tooltip: this.detachedRegistered
            ? this.$t('tooltips.focus_window')
            : this.$t('tooltips.open_in_separate_window'),
          tstId: 'open_in_new',
        },
        {
          icons: ['mdi-pin-outline', 'mdi-pin'],
          onClick: this.togglePinned,
          value: this.pinned,
          tooltip: this.pinned ? this.$t('tooltips.gmf_unpinn') : this.$t('tooltips.gmf_pinn'),
          tstId: 'pin',
        },
      ].filter(i => !i.is_hidden)
    },
    tabIsGwiz() {
      return this.libtab === 'gwiz'
    },
    libtab() {
      return this.$store.getters[SNK('query_solution_type', this.namespace)] === 'custom'
        ? this.$store.getters[SNK('query_gmfs_solution_tab', this.namespace)] ||
            this.libOptions.find(item => item.payload !== 'gwiz').payload
        : 'gwiz'
    },
    libOptions() {
      const setTab = params => {
        const args = {
          namespace: this.namespace,
          channelId: this.channelId,
          ...params,
        }
        this.$store.dispatch(SNK('sol_switchSolTypeNEW', this.namespace), args)
      }
      const res = [
        {
          payload: 'gwiz',
          fn: () =>
            setTab({
              gmfs_solution_tab: 'gwiz_sols',
              solType: 'gwiz',
            }),
        },
        {
          payload: 'ai_sols',
          fn: () =>
            setTab({
              gmfs_solution_tab: 'ai_sols',
              solType: 'custom',
            }),
        },
        this.$store.getters.sol_elite.can_use &&
          this.$route.name === 'StudySolutions' && {
            payload: 'ai_reports',
            fn: () =>
              setTab({
                gmfs_solution_tab: 'ai_reports',
                solType: 'custom',
              }),
          },
      ].filter(Boolean)
      return res
    },
  },
  watch: {
    visible(value) {
      if (value) {
        // Without repostitioning the preview it won't show if it's enabled and the floating dialog is reopened
        setTimeout(() => this.checkPosition(), 100)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
a.surveylink {
  &:visited {
    color: red;
  }
}

.flgmfslctr {
  z-index: 8;
  min-height: 0;
  min-width: 0;
  flex: 1;
  display: flex;
  --title-height: 2.5em;
  &_close {
    margin-left: 2em;
    position: absolute;
    right: 0.5em;
  }
  &_gmf-selector {
    height: 42em;
    width: 66.125rem;
    min-width: 100%;
    max-width: 100%;
    min-height: 100%;
    max-height: 100%;
    z-index: 1;
    display: flex;
    &:hover {
      z-index: 3;
    }
  }
  &_inner {
    padding: 0.25rem;
    border-radius: 8px;
    background: var(--clr-black-0);
    box-shadow:
      0px 0px 0px 1px var(--clr-black-2),
      var(--menu-shadow);
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }
  &_tabbox {
    height: 100%;
    width: 100%;
  }
  &_preview {
    padding: 0 1rem;
    position: fixed;
    z-index: 0;
    transition: 0.44s;
    min-width: 22rem;
    min-width: 22rem;
    &_inner {
      border-radius: 4px;
      background: var(--clr-black-1);
      box-shadow: 0px 0px 0px 1px var(--clr-black-2);
      padding: 0.5rem 1rem 1rem 1rem;

      // border-radius: 16px;
      // background: var(--clr-black-0);
      // padding: 0.5rem 0 0 0;
    }
  }
  &_title {
    overflow: hidden;
    padding: 0 0.5em;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: var(--title-height);
    line-height: 1.1;
    z-index: 2;
    position: relative;
    height: 100%;
    &_text {
      width: 100%;
      font-size: 1em;
      font-weight: 600;
    }
    &_controls {
      display: flex;
      margin: 0 4rem 0 1rem;
      gap: 1rem;
      &_item {
        &:last-child {
          &:after {
            display: none;
          }
        }
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
